/**
 * Lessons: a named idea, then a position where that idea is the answer.
 *
 * The other teaching surfaces all start from a position and work out what is
 * true of it. This one starts from an *idea* — take the centre, block that
 * square, build two threats at once, set a trap — explains it, and then hunts
 * for a position where that idea is exactly what the board is asking for.
 *
 * The difference matters. A player who has been told "column 4 was better"
 * fifty times has fifty facts; a player who has been shown the double threat
 * once, named, and then made to find it three times has a thing they can look
 * for. Naming is the point: an idea you cannot name is one you cannot go
 * looking for.
 *
 * **Every lesson's answer is computed, never stored.** A drill is judged by
 * asking the board which moves embody the idea and checking whether the played
 * move is one of them — not by comparing against a single remembered column.
 * Several moves often qualify, and marking a correct one wrong would teach a
 * falsehood about the game. This is the same rule the puzzles and the review's
 * reasoning follow, and it is the reason none of these lessons can drift out of
 * step with the engine.
 *
 * Spec-driven: a "line" is whatever `terminal.win` declares and every fact is
 * read through the interpreter, so an authored 9x7 game gets the same
 * curriculum with no new code. The one claim that is *not* derived — Connect 4
 * being a solved game — is fenced behind `isStandardConnect4` and stated only
 * when the spec really is that game.
 *
 * Purity: no I/O, no clock of its own, seeded randomness. The same seed yields
 * the same drill.
 */
import { applyMove, boardSize, createInitial, legalMoves } from '../spec/interpreter.ts';
import type { SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { readPosition } from './coach.ts';
import { cellReach, isStandardConnect4, landingCell } from './openings.ts';
import { fallsToMover, parityDangerColumns } from './parity.ts';
import { winningMovesFor } from './reasons.ts';
import { MATE_THRESHOLD, isDecisive, searchPosition } from './search.ts';

/*
 * The parity arithmetic lives in `parity.ts`, which owns it for the counting
 * trainer as well as for this lesson. Re-exported here because this module is
 * where it was first published and callers should not have to care that it
 * moved.
 */
export { fallsToMover, parityDangerColumns };

/** The ideas worth naming, in the order a player meets them. */
export const LESSON_IDS = [
  'centre_control',
  'block_the_threat',
  'dont_walk_in',
  'double_threat',
  'set_a_trap',
  'count_the_gaps',
] as const;

export type LessonId = (typeof LESSON_IDS)[number];

export interface Lesson {
  readonly id: LessonId;
  /** Short enough to be a button. */
  readonly title: string;
  /** The idea itself, in one or two sentences. Shown before the first drill. */
  readonly idea: string;
  /** What to do on the board, as an instruction. */
  readonly task: string;
  /**
   * Why it works, shown after a correct answer.
   *
   * Separate from `idea` on purpose: the explanation lands differently once the
   * player has just done the thing.
   */
  readonly why: string;
}

/**
 * The curriculum.
 *
 * Ordered by what a player actually meets: you learn where to play before you
 * learn what to stop, you learn to stop things before you learn to build them,
 * and the double threat comes before the trap because a trap is a double threat
 * you have not built yet.
 */
export const LESSONS: Readonly<Record<LessonId, Lesson>> = Object.freeze({
  centre_control: {
    id: 'centre_control',
    title: 'Play the centre',
    idea:
      'Not every square is worth the same. A disc in the middle sits in far more winning lines than one on the edge, so it does more work for the rest of the game.',
    task: 'Play the square that sits in the most winning lines.',
    why: 'The middle column touches the most ways to win. Every line you are part of is a line you might finish, and one you have denied them.',
  },
  block_the_threat: {
    id: 'block_the_threat',
    title: 'Block the threat',
    idea:
      'Before anything else, check what happens if you do nothing. If they have three in a row with an open end, that square is the only move on the board.',
    task: 'Find the square you have to take, or lose.',
    why: 'Everything else is worth nothing if they finish first. Look for their threats before you look for your own plan.',
  },
  dont_walk_in: {
    id: 'dont_walk_in',
    title: "Don't hand them the square",
    idea:
      'Your disc fills a square — and hands them the one directly above it. A move that looks harmless can be the piece that lets them finish.',
    /*
     * Asked as "find the losing move" rather than "play a safe one". Six of
     * seven moves are usually safe, so choosing a safe one is something a
     * player passes by guessing — and passing by guessing teaches nothing.
     * Pointing at the one that loses is the actual skill.
     */
    task: 'One move here loses on the spot. Point at it.',
    why: 'Before you play, look at the square your disc creates on top of itself. If that square finishes their line, the move loses however good it looks.',
  },
  double_threat: {
    id: 'double_threat',
    title: 'Two threats at once',
    idea:
      'One threat gets blocked. Two threats at the same time cannot both be blocked — they answer one, you play the other. This is how games are actually won.',
    task: 'Find the move that leaves you two ways to win.',
    why: 'They have one disc per turn. Give them two problems and one of them survives, which is why the double threat decides almost every game between players who can both count to three.',
  },
  /*
   * The expert lesson, and the only one about the future rather than the board.
   *
   * The wording is deliberately the *mechanism* rather than the famous
   * "odd rows favour player one" slogan. Measured against our own exact solver,
   * that slogan holds in only about 81% of midgame positions, while the count
   * below is exact - see the verification note in DESIGN.md. Teaching the
   * slogan would be teaching something we had proven false.
   */
  count_the_gaps: {
    id: 'count_the_gaps',
    title: 'Count the squares below',
    idea:
      'A winning square out of reach still belongs to someone. Count the empty squares beneath it: if that count is even, it falls to whoever moves next in that column. Fill in below their square and you hand them the game.',
    task: 'One column has their winning square waiting above. Point at the column you must not fill.',
    why: 'Columns fill from the bottom, one disc each, turn by turn. That makes the owner of a square arithmetic, not luck: count the gaps beneath it and you know whose it is long before anyone can reach it.',
  },

  set_a_trap: {
    id: 'set_a_trap',
    title: 'Set a trap',
    idea:
      'The strongest move often threatens nothing at all. You quietly place the disc that turns almost any reply into a losing one — and they have no line to block, so nothing warns them.',
    task: 'Play the quiet move that leaves them only one escape.',
    why: 'A visible threat gets blocked, because it is visible. A trap works because there is nothing to see: whatever looks natural walks into a double threat next turn.',
  },
});

/**
 * A position where one lesson's idea is the answer.
 *
 * The position is stored as the moves that built it, so it is replayed through
 * the interpreter and cannot describe a board the game could not reach.
 */
export interface Drill {
  readonly lesson: LessonId;
  readonly id: string;
  readonly firstSeat: number;
  readonly setup: readonly number[];
  /** The seat solving. Always the seat to move. */
  readonly seat: number;
  /**
   * Every move that embodies the idea. Usually one, sometimes several.
   *
   * Carried so the screen can accept any of them, and so a hint has something
   * to point at without a second search.
   */
  readonly answers: readonly number[];
  /**
   * For `dont_walk_in`, the moves that lose. The lesson is about avoiding
   * these, so naming them is how the explanation gets specific.
   */
  readonly refuted: readonly number[];
  /** How many winning lines the recommended square sits in. Positional lessons only. */
  readonly lines: number | null;
  /** The fewest any playable square reaches, which is what makes `lines` mean something. */
  readonly fewest: number | null;
  /** True only when the spec is the game the solved-game result is about. */
  readonly solvedGame: boolean;
}

/** Replay from an empty board. Null when the moves do not describe a live position. */
export function replayDrill<S extends RulesSpec>(
  rules: S,
  firstSeat: number,
  setup: readonly number[],
): State<S> | null {
  let state = createInitial(rules, firstSeat as SeatOf<S>);
  for (const param of setup) {
    if (state.status.kind !== 'in_play') return null;
    const applied = applyMove(rules, state, state.toMove, { param });
    if (!applied.ok) return null;
    state = applied.state;
  }
  return state.status.kind === 'in_play' ? state : null;
}

/** True when a fork is available to `seat` right now. */
function forkAvailable<S extends RulesSpec>(rules: S, state: State<S>, seat: number): boolean {
  for (const move of legalMoves(rules, state)) {
    const applied = applyMove(rules, state, seat as SeatOf<S>, move);
    if (!applied.ok || applied.state.status.kind !== 'in_play') continue;
    if (winningMovesFor(rules, applied.state, seat as SeatOf<S>).length >= 2) return true;
  }
  return false;
}

/**
 * Moves that set a trap: quiet now, and almost anything they do loses.
 *
 * The quietness is the whole lesson and the reason this is not just "make a
 * threat". A move that leaves a winning square on the board is a threat: it is
 * visible, and it gets blocked. A trap leaves *nothing to block*, and still
 * punishes all but one reply — with a double threat, one move later.
 *
 * Measured rather than asserted: for every reply they have, the position is
 * played out and asked whether a fork is then available. A move only counts as
 * a trap when at most one reply escapes.
 */
export function trapSettingMoves<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): readonly number[] {
  const me = state.toMove;
  const reading = readPosition(rules, state);
  // A trap is a quiet idea. With a win or a forced block on the board the
  // position is asking a different, louder question.
  if (reading.winsNow.length > 0 || reading.mustBlock.length > 0) return [];

  const setters: number[] = [];
  for (const param of reading.safe) {
    const applied = applyMove(rules, state, me, { param });
    if (!applied.ok || applied.state.status.kind !== 'in_play') continue;

    // Nothing on the board for them to see, or it is a threat and not a trap.
    if (winningMovesFor(rules, applied.state, me).length > 0) continue;

    const after = applied.state;
    const them = after.toMove;
    const replies = legalMoves(rules, after);
    // With very few replies "all but one" stops meaning anything.
    if (replies.length < 3) continue;

    let punished = 0;
    for (const reply of replies) {
      const answered = applyMove(rules, after, them, reply);
      if (!answered.ok || answered.state.status.kind !== 'in_play') continue;
      // A reply that creates their own threat is an escape: we would have to
      // answer it rather than carry on building.
      if (winningMovesFor(rules, answered.state, them).length > 0) continue;
      if (forkAvailable(rules, answered.state, me)) punished += 1;
    }

    if (punished >= replies.length - 1 && punished >= 2) setters.push(param);
  }
  return setters;
}

/**
 * The moves that embody a lesson in this position, or empty when it does not
 * apply here.
 *
 * This is the single source of truth for both hunting and judging: a drill is
 * found by asking every position this question, and answered by asking it
 * again. They cannot disagree.
 */

/**
 * Whether a move loses, drawn from exact search rather than from parity.
 *
 * The parity count is what the lesson *teaches*; it is not what the lesson
 * *asserts*. Measured against this same solver, the popular reading of parity
 * is right about four times in five, so a drill whose answer came from the
 * heuristic would eventually mark a good move wrong. The heuristic proposes,
 * the search decides.
 *
 * Returns null when the position could not be solved inside the budget.
 */
function exactOutcome<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  budgetMs: number,
): 'lost' | 'drawn' | 'won' | null {
  if (state.status.kind !== 'in_play') {
    return state.status.kind === 'draw' ? 'drawn' : 'lost';
  }
  const empty = emptyCount(rules, state);
  const result = searchPosition(rules, state, { budgetMs, maxDepth: empty });
  const solved =
    result.completed &&
    result.moves.length > 0 &&
    (result.depth >= empty || result.moves.every((entry) => isDecisive(entry.score)));
  if (!solved) return null;

  // Scored from the point of view of whoever is to move in `state`, which after
  // our move is the opponent: their win is our loss.
  const best = result.moves[0]?.score ?? 0;
  if (best >= MATE_THRESHOLD) return 'lost';
  if (best <= -MATE_THRESHOLD) return 'won';
  return 'drawn';
}

function emptyCount<S extends RulesSpec>(rules: S, state: State<S>): number {
  const size = boardSize(rules);
  let empty = 0;
  for (let index = 0; index < size; index += 1) {
    if (state.board[index] === rules.cells.empty) empty += 1;
  }
  return empty;
}

/*
 * The window where this lesson actually lives.
 *
 * Measured, not guessed: every drill the hunt has ever produced sat at 16 or 17
 * empty squares. Below fifteen the board is too full for a far-off square to
 * still be in question, and above eighteen an exact answer is out of reach - so
 * searching outside the window only spends time proving nothing.
 */
const PARITY_MIN_EMPTY = 15;
const PARITY_MAX_EMPTY = 18;
const PARITY_BUDGET_MS = 1200;

export function answersFor<S extends RulesSpec>(
  rules: S,
  lesson: LessonId,
  state: State<S>,
): readonly number[] {
  if (state.status.kind !== 'in_play') return [];
  const reading = readPosition(rules, state);

  switch (lesson) {
    case 'centre_control': {
      // Only while the board is open enough for "where is the value" to be the
      // real question; after that the position is concrete.
      if (reading.phase !== 'opening') return [];
      if (reading.winsNow.length > 0 || reading.mustBlock.length > 0) return [];
      const reach = cellReach(rules);
      let best = -1;
      const ranked: { col: number; lines: number }[] = [];
      for (let col = 0; col < rules.board.cols; col += 1) {
        const cell = landingCell(rules, state, col);
        if (cell === -1) continue;
        const lines = reach[cell] ?? 0;
        ranked.push({ col, lines });
        if (lines > best) best = lines;
      }
      // A drill needs a real choice: if every square reaches the same, there is
      // nothing being taught.
      const fewest = ranked.reduce((low, entry) => Math.min(low, entry.lines), Number.MAX_SAFE_INTEGER);
      if (best <= fewest) return [];
      return ranked.filter((entry) => entry.lines === best).map((entry) => entry.col);
    }

    case 'block_the_threat':
      // Exactly one square, or the lesson is "you have already lost".
      if (reading.winsNow.length > 0) return [];
      return reading.mustBlock.length === 1 ? reading.mustBlock : [];

    /*
     * The answer is the move that *loses*, because the drill asks the player to
     * point at it. Exactly one, so there is a single answer to be right about:
     * with two losing moves the question "which one" has no honest answer, and
     * with none there is nothing to find.
     */
    case 'dont_walk_in':
      if (reading.winsNow.length > 0 || reading.mustBlock.length > 0) return [];
      if (reading.traps.length !== 1 || reading.safe.length === 0) return [];
      return reading.traps.map((trap) => trap.param);

    case 'double_threat':
      if (reading.winsNow.length > 0 || reading.mustBlock.length > 0) return [];
      return reading.forks;

    case 'set_a_trap':
      return trapSettingMoves(rules, state);

    /*
     * Point at the column that must not be filled.
     *
     * Exactly one, so there is a single answer to be right about, and only when
     * a safe alternative exists - a drill where every move is the wrong move
     * teaches nothing but despair.
     */
    /*
     * Point at the column that must not be filled.
     *
     * Parity proposes the candidate; exact search decides the answer. Both must
     * agree, so the drill is never issued on a position where the count and the
     * truth part company - and the player is never marked wrong for being right.
     */
    case 'count_the_gaps': {
      if (reading.winsNow.length > 0 || reading.mustBlock.length > 0) return [];
      if (reading.legal.length < 3) return [];
      const openSquares = emptyCount(rules, state);
      if (openSquares < PARITY_MIN_EMPTY || openSquares > PARITY_MAX_EMPTY) return [];

      const candidates = parityDangerColumns(rules, state);
      if (candidates.length !== 1) return [];
      const suspect = candidates[0];
      if (suspect === undefined || !reading.legal.includes(suspect)) return [];
      // A move that loses at once belongs to the trap lesson, not this one.
      if (reading.traps.some((trap) => trap.param === suspect)) return [];

      const seat = state.toMove;

      /*
       * The suspect first, and bail the moment it survives.
       *
       * Most candidates the count proposes are not actually losing, and
       * checking them costs one search instead of seven. Ordering the cheap
       * rejection first is what takes the hunt from seconds to milliseconds.
       */
      const suspectMove = applyMove(rules, state, seat, { param: suspect });
      if (!suspectMove.ok) return [];
      if (exactOutcome(rules, suspectMove.state, PARITY_BUDGET_MS) !== 'lost') return [];

      // Only now is it worth proving the rest survive, which is what makes the
      // answer unique rather than merely correct.
      let survivable = 0;
      for (const param of reading.legal) {
        if (param === suspect) continue;
        const applied = applyMove(rules, state, seat, { param });
        if (!applied.ok) return [];
        const outcome = exactOutcome(rules, applied.state, PARITY_BUDGET_MS);
        if (outcome === null || outcome === 'lost') return [];
        survivable += 1;
      }

      return survivable > 0 ? [suspect] : [];
    }
  }
}

export interface DrillOptions {
  readonly seed?: number;
  readonly budgetMs?: number;
  readonly now?: () => number;
  /**
   * Qualifying positions to walk past before accepting one.
   *
   * Without this the hunt always returns the *first* position that teaches the
   * lesson, which for `centre_control` is the empty board every single time —
   * one drill, repeated forever. Skipping a seeded number of them makes a run
   * of drills genuinely different while staying reproducible.
   */
  readonly skip?: number;
}

const DEFAULT_DRILL_BUDGET_MS = 5000;

/*
 * Parity is an order of magnitude dearer than the rest of the curriculum.
 *
 * It is the only lesson whose answer has to be proven by exact search rather
 * than read off the board, and the positions that carry it are rare - measured
 * at roughly eleven seconds to find one. The allowance lives here rather than
 * at the call sites so no caller can quietly ask for the impossible and be told
 * the lesson does not exist.
 */
const LESSON_BUDGET_MS: Partial<Record<LessonId, number>> = { count_the_gaps: 30_000 };

/** Deterministic PRNG. A shared `Math.random` would make drills unreproducible. */
function seededRandom(seed: number): () => number {
  let state = (seed >>> 0) || 1;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 2 ** 32;
  };
}

/**
 * A position is worth drilling only if it is a real one.
 *
 * `centre_control` is the exception: its whole subject is the empty and
 * near-empty board, so the usual "wait for a real position" floor would rule
 * out the only positions it can teach on.
 */
function readyFor<S extends RulesSpec>(rules: S, lesson: LessonId, state: State<S>): boolean {
  if (lesson === 'centre_control') return true;
  const size = boardSize(rules);
  let filled = 0;
  for (let index = 0; index < size; index += 1) {
    if (state.board[index] !== rules.cells.empty) filled += 1;
  }
  // Four discs is the earliest anything tactical can exist on any board worth
  // playing, and the fullness cap keeps drills off cramped end positions.
  return filled >= 4 && filled <= size * 0.65;
}

/**
 * Walk a plausible game, examining every position it passes through.
 *
 * The walkers take a win in front of them and block a loss in front of them,
 * for the same reason the puzzle hunt does: purely random play produces boards
 * no human would reach, and a lesson taught on one is a pattern that will never
 * come up. Everything else drifts towards the middle.
 */
function wander<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  random: () => number,
): { readonly param: number } | undefined {
  const moves = legalMoves(rules, state);
  if (moves.length === 0) return undefined;

  const seat = state.toMove;
  const mine = winningMovesFor(rules, state, seat);
  const takeIt = mine[0];
  if (takeIt !== undefined) return { param: takeIt };

  const others = rules.seats.filter((candidate) => candidate !== seat);
  if (rules.seats.length === 2 && others[0] !== undefined) {
    const theirs = winningMovesFor(rules, state, others[0] as SeatOf<S>);
    const block = theirs[0];
    if (theirs.length === 1 && block !== undefined) return { param: block };
  }

  const middle = (rules.board.cols - 1) / 2;
  const weights = moves.map((move) => 1 / (1 + Math.abs(move.param - middle)));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  if (total <= 0) return moves[0];

  let ticket = random() * total;
  for (const [index, weight] of weights.entries()) {
    ticket -= weight;
    if (ticket <= 0) return moves[index];
  }
  return moves[moves.length - 1];
}

/** Positional colour for the drill card: how much the answer square reaches. */
function reachOf<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  answer: number,
): { lines: number | null; fewest: number | null } {
  const reach = cellReach(rules);
  const cell = landingCell(rules, state, answer);
  if (cell === -1) return { lines: null, fewest: null };

  let fewest = Number.MAX_SAFE_INTEGER;
  for (let col = 0; col < rules.board.cols; col += 1) {
    const other = landingCell(rules, state, col);
    if (other === -1) continue;
    fewest = Math.min(fewest, reach[other] ?? 0);
  }
  return {
    lines: reach[cell] ?? 0,
    fewest: fewest === Number.MAX_SAFE_INTEGER ? null : fewest,
  };
}

/** Build a drill from a position already known to teach the lesson. */
function drillAt<S extends RulesSpec>(
  rules: S,
  lesson: LessonId,
  firstSeat: number,
  setup: readonly number[],
  state: State<S>,
  answers: readonly number[],
): Drill {
  const first = answers[0] ?? 0;
  const { lines, fewest } = reachOf(rules, state, first);
  const reading = readPosition(rules, state);

  return Object.freeze({
    lesson,
    id: `${lesson}:${firstSeat}:${setup.join('')}`,
    firstSeat,
    setup: Object.freeze([...setup]),
    seat: state.toMove,
    answers: Object.freeze([...answers]),
    refuted: Object.freeze(reading.traps.map((trap) => trap.param)),
    lines,
    fewest,
    solvedGame: isStandardConnect4(rules),
  });
}

/**
 * Hunt for a position where this lesson's idea is the answer.
 *
 * Returns null when the budget runs out rather than overrunning: some lessons
 * are rare — a genuine trap turns up in well under one position in a hundred —
 * and a screen that froze while looking would be worse than one that says it
 * could not find anything.
 */
export function findDrill<S extends RulesSpec>(
  rules: S,
  lesson: LessonId,
  options: DrillOptions = {},
): Drill | null {
  const now = options.now ?? (() => Date.now());
  const deadline =
    now() + (options.budgetMs ?? LESSON_BUDGET_MS[lesson] ?? DEFAULT_DRILL_BUDGET_MS);
  const random = seededRandom(options.seed ?? 1);
  const firstSeat = rules.seats[0] ?? 1;

  /*
   * How many qualifying positions to walk past first.
   *
   * Derived from the seed rather than taken as a separate knob by default, so
   * a caller that only varies the seed still gets varied drills — which is the
   * common case and the one that was broken.
   */
  let toSkip = options.skip ?? Math.floor(random() * 6);

  /** The best drill seen while skipping, so a hunt never comes back empty-handed. */
  let fallback: Drill | null = null;

  while (now() < deadline) {
    let state: State<S> = createInitial(rules, firstSeat as SeatOf<S>);
    const setup: number[] = [];

    while (state.status.kind === 'in_play') {
      if (now() >= deadline) return fallback;

      if (readyFor(rules, lesson, state)) {
        const answers = answersFor(rules, lesson, state);
        if (answers.length > 0) {
          const drill = drillAt(rules, lesson, firstSeat, setup, state, answers);
          if (toSkip <= 0) return drill;
          // Keep the first one found: if the budget expires while skipping, a
          // repeated drill still beats no drill at all.
          fallback ??= drill;
          toSkip -= 1;
        }
      }

      const chosen = wander(rules, state, random);
      if (!chosen) break;
      const applied = applyMove(rules, state, state.toMove, chosen);
      if (!applied.ok) break;
      setup.push(chosen.param);
      state = applied.state;
    }
  }

  return fallback;
}

/** What a drill answer was. */
export type DrillVerdict =
  | { readonly kind: 'right' }
  /** Wrong, and for `dont_walk_in` this move is the one that actually loses. */
  | { readonly kind: 'wrong'; readonly walkedIn: boolean }
  | { readonly kind: 'illegal' };

/**
 * Judge one answer.
 *
 * Asks the board rather than the stored drill, so any move that embodies the
 * idea is accepted. The stored answers exist for hints and for tests; they are
 * not the authority here, which is what keeps a correct-but-unexpected move
 * from being marked wrong.
 */
export function judgeDrill<S extends RulesSpec>(
  rules: S,
  lesson: LessonId,
  state: State<S>,
  param: number,
): DrillVerdict {
  if (state.status.kind !== 'in_play') return { kind: 'illegal' };
  if (!legalMoves(rules, state).some((move) => move.param === param)) {
    return { kind: 'illegal' };
  }

  if (answersFor(rules, lesson, state).includes(param)) return { kind: 'right' };

  const walkedIn = readPosition(rules, state).traps.some((trap) => trap.param === param);
  return { kind: 'wrong', walkedIn };
}
